'use client';
import { authHref, safeAuthReturn } from '@/shared/lib/auth-return';

import { redirect } from 'next/navigation';
import { useMetrica } from 'next-yandex-metrica';
import { useActionState, useEffect } from 'react';

import { useToast } from '@/features/toast';
import {
	Button,
	Checkbox,
	LockIcon,
	OptIn,
	TextFiled,
	UserIcon,
} from '@/shared';
import {
	consentToAdvertisingMailingDocument,
	YM_GOALS,
} from '@/shared/constants';
import { urlBuilder } from '@/shared/utils';

import { registerAction, registerConfirmAction } from '../action';
import { useResendCodeAction } from '../hooks';
import { ResendCode } from './resend-code';

export function RegisterConfirmForm({
	email,
	promoCode,
	redirectPath,
}: {
	email: string;
	redirectPath?: string;
	promoCode?: string;
}) {
	const { reachGoal } = useMetrica();
	const { addToast } = useToast();
	const [state, formAction, pending] = useActionState(
		registerConfirmAction,
		{},
	);
	const [resendKey, resendAction, resending] = useResendCodeAction(
		registerAction,
		{ email, redirect: safeAuthReturn(redirectPath) ?? '' },
	);

	useEffect(() => {
		if (state.message) {
			addToast('Ошибка', state.message, 'error');
		}
		if (state.success) {
			reachGoal(YM_GOALS.USER_REGISTRATION);
			redirect(authHref(urlBuilder.login({ register: 'true' }), redirectPath));
		}
	}, [addToast, reachGoal, state, redirectPath]);

	return (
		<form
			className="flex flex-1 flex-col gap-8 md:flex-grow-0"
			action={formAction}
		>
			<input
				type="hidden"
				name="redirect"
				value={safeAuthReturn(redirectPath) ?? ''}
			/>
			<fieldset>
				<TextFiled type="hidden" name="email" value={email} />
				<TextFiled type="hidden" name="promoCode" value={promoCode} />

				<OptIn
					label={
						<>
							<span>Код верификации, присланный на email</span>
							<span className="mt-0.5 block text-xs text-gray-3">{email}</span>
						</>
					}
					name="emailCode"
					hint={
						<ResendCode
							key={resendKey}
							loading={resending}
							onClick={resendAction}
						/>
					}
					defaultValue={(state.formData?.get('emailCode') || '') as string}
					size="lg"
					invalid={!!state?.errors?.emailCode}
				/>
			</fieldset>

			<fieldset className="mb-auto space-y-3">
				<TextFiled
					type="text"
					placeholder="Логин"
					name="login"
					autoComplete="username"
					icon={<UserIcon className="size-6" />}
					defaultValue={(state.formData?.get('login') || '') as string}
					invalid={!!state?.errors?.login}
					hint={state?.errors?.login?.reduce((_, err) => err, '')}
					size="lg"
					required
				/>
				<TextFiled
					type="password"
					placeholder="Пароль"
					name="password"
					autoComplete="new-password"
					icon={<LockIcon className="size-6" />}
					defaultValue={(state.formData?.get('password') || '') as string}
					invalid={!!state?.errors?.password}
					hint={state?.errors?.password?.reduce((_, err) => err, '')}
					size="lg"
					required
				/>
				<TextFiled
					type="password"
					placeholder="Повторите пароль"
					name="confirmPassword"
					autoComplete="new-password"
					icon={<LockIcon className="size-6" />}
					defaultValue={
						(state.formData?.get('confirmPassword') || '') as string
					}
					invalid={!!state?.errors?.confirmPassword}
					hint={state?.errors?.confirmPassword?.reduce((_, err) => err, '')}
					size="lg"
					required
				/>
			</fieldset>

			<Checkbox
				name="advertisingMail"
				value="true"
				label={
					<>
						Я{' '}
						<a
							href={consentToAdvertisingMailingDocument.url}
							className="text-accent hover:underline"
							target="_blank"
						>
							согласен
						</a>{' '}
						получать рассылку с полезными материалами и анонсами. Никакого
						спама, отписаться можно в 1 клик
					</>
				}
				defaultChecked={false}
				labelSize="xs"
			/>

			<Button type="submit" loading={pending} size="lg" block>
				Зарегистрироваться
			</Button>
		</form>
	);
}
